import React from 'react';
import { Container, Accordion, Badge } from 'react-bootstrap';
import AppHeader from '../../components/Common/AppHeader';
import AppFooter from '../../components/Common/AppFooter';

const faqData = [
  {
    category: "Getting Started",
    items: [
      {
        q: "How do I start a new course?",
        a: "The Admin creates a Program and Project, assigns an SME, and the SME defines the curriculum before starting orchestration."
      },
      {
        q: "Who should be assigned as SME?",
        a: "A faculty member or domain expert responsible for curriculum design, validation, and academic quality."
      },
      {
        q: "Can multiple SMEs work on the same project?",
        a: "Yes, but it is recommended to have a primary SME to maintain consistency in structure and content. At any point of time, only one SME can access the project."
      },
      {
        q: "Do I need prior experience with AI tools?",
        a: "No. The platform is designed for academic users and does not require technical expertise."
      },
      {
        q: "Can I reuse an existing program structure?",
        a: "Yes. Programs and curriculum structures can be reused or adapted for new projects."
      }
    ]
  },
  {
    category: "Content Generation",
    items: [
      {
        q: "Can I generate the entire course at once?",
        a: "No. It is recommended to generate content one unit at a time to maintain quality and consistency."
      },
      {
        q: "How many reference books should I use for a course / subject?",
        a: "Use 2–3 well-structured textbooks. Avoid using too many sources to prevent inconsistency."
      },
      {
        q: "Can I change reference books after starting?",
        a: "No, It is not possible as it affect consistency across generated units."
      },
      {
        q: "Why does my content feel repetitive?",
        a: "This usually happens when topics overlap or are too broad. Refining topic definitions improves output quality."
      },
      {
        q: "What happens if my topics are too large?",
        a: "Large topics can lead to generic or repetitive content. Breaking them into smaller concepts improves clarity."
      },
      {
        q: "Can I regenerate a unit?",
        a: "Yes. Units can be regenerated after refining curriculum or references."
      }
    ]
  },
  {
    category: "Standards & Quality",
    items: [
      {
        q: "Can I change Content Standards midway?",
        a: "No. Changing standards mid-project can break alignment across content and assessments."
      },
      {
        q: "What happens if I mix taxonomy standards?",
        a: "Mixing taxonomy standards may result in inconsistent learning outcomes and assessment difficulty."
      },
      {
        q: "How are learning outcomes defined?",
        a: "They are aligned with the selected taxonomy and guide both content depth and assessments."
      },
      {
        q: "How does the system ensure academic quality?",
        a: "Content is grounded in reference materials and structured using academic standards and taxonomy frameworks."
      },
      {
        q: "Can I customize learning outcomes?",
        a: "Yes. SMEs can refine or adjust learning outcomes based on institutional requirements."
      }
    ]
  },
  {
    category: "Workflow & System Behavior",
    items: [
      {
        q: "What is CKB?",
        a: "CKB is a structured concept map extracted from your reference books to guide content generation."
      },
      {
        q: "Why is my unit taking time to generate?",
        a: "Generation time depends on unit complexity, number of topics, and reference material size."
      },
      {
        q: "Why can’t I start orchestration?",
        a: "Ensure your curriculum is finalized, CKB graph is generated and required inputs are completed before starting."
      },
      {
        q: "What should I do if a unit looks incomplete?",
        a: "Review the curriculum and references, then regenerate or refine the unit."
      },
      {
        q: "Can I pause or stop the process?",
        a: "Yes. You can stop and resume the workflow depending on system state and progress. You Can't pause in-between the running process."
      },
      {
        q: "What happens if generation fails?",
        a: "Retry the process after verifying inputs such as curriculum structure and reference materials."
      }
    ]
  },
  {
    category: "Output & Export",
    items: [
      {
        q: "Can I edit content before exporting?",
        a: "Yes. You can review and refine content in the editor before export."
      },
      {
        q: "What formats are supported?",
        a: "Content can be exported in DOCX, PDF, and formats suitable for LMS integration."
      },
      {
        q: "Is the content ready for teaching?",
        a: "Yes, but SME review is recommended to add institutional context and finalize quality."
      },
      {
        q: "Can I export individual units?",
        a: "Yes. Units can be exported separately or as a complete course."
      },
      {
        q: "Are images, tables, and formulas included in export?",
        a: "Yes. The system ensures proper formatting for visual and structured elements."
      },
      {
        q: "Can exported content be used directly in LMS platforms?",
        a: "Yes. The output is structured to support integration with most LMS systems."
      }
    ]
  }
];

const FAQ = () => (
  <div className="bg-light min-vh-100 d-flex flex-column">
    <AppHeader />

    <Container className="py-5 flex-grow-1">
      <div className="text-center mb-5">
        <Badge bg="primary">AcademicOS Support</Badge>
        <h1 className="fw-bold mt-2">Frequently Asked Questions</h1>
        <p className="text-muted">
          Common questions about using AcademicOS for curriculum design, content generation, and academic delivery.
        </p>
      </div>

      {faqData.map((section, idx) => (
        <div key={idx} className="mb-4">
          <h5 className="fw-bold mb-3">{section.category}</h5>

          <Accordion>
            {section.items.map((item, i) => (
              <Accordion.Item eventKey={i.toString()} key={i}>
                <Accordion.Header>{item.q}</Accordion.Header>
                <Accordion.Body>{item.a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      ))}
    </Container>

    <AppFooter />
  </div>
);

export default FAQ;
